import {
  ArrowLeftOnRectangleIcon,
  ArrowTopRightOnSquareIcon,
  Bars3Icon,
  ChartBarIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  HomeIcon,
  KeyIcon,
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  QuestionMarkCircleIcon,
  SparklesIcon,
  TruckIcon,
  UsersIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'

import { API_BASE } from '#api/client.js'

const navItems = [
  { to: '/tenants', label: 'Tenants', icon: HomeIcon },
  { to: '/accounts', label: 'Accounts', icon: UsersIcon },
  { to: '/carrier-connections', label: 'Carrier Connections', icon: KeyIcon },
  { to: '/quotes', label: 'Quotes', icon: CurrencyDollarIcon },
  { to: '/ebol', label: 'eBOL', icon: DocumentTextIcon },
  { to: '/tenders', label: 'Tenders', icon: PaperAirplaneIcon },
  { to: '/tracking', label: 'Tracking Subscriptions', icon: TruckIcon },
  { to: '/documents', label: 'Document Subscriptions', icon: DocumentTextIcon },
  { to: '/get-trackings', label: 'Tracking Lookups', icon: TruckIcon },
  { to: '/get-documents', label: 'Document Lookups', icon: DocumentTextIcon },
  { to: '/profinder', label: 'ProFinder', icon: SparklesIcon },
  { to: '/traces', label: 'Traces', icon: MagnifyingGlassIcon },
]

const reportItems = [
  { to: '/tracking-reports', label: 'Tracking Reports', icon: ChartBarIcon },
  {
    to: '/documents-reports',
    label: 'Documents Reports',
    icon: ChartBarIcon,
  },
  {
    to: '/carrier-requests-report',
    label: 'Carrier Requests Report',
    icon: ChartBarIcon,
  },
  {
    to: '/tracking-unmapped',
    label: 'Unmapped Track Events',
    icon: QuestionMarkCircleIcon,
  },
  {
    to: '/unmapped-errors',
    label: 'Unmapped EBOL/Tender Errors',
    icon: ExclamationTriangleIcon,
  },
  {
    to: '/quotes-report',
    label: 'Quotes Report',
    icon: ChartBarIcon,
  },
  {
    to: '/profinder-report',
    label: 'ProFinder Report',
    icon: SparklesIcon,
  },
]

export const AppLayout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  return (
    <div className="min-h-screen bg-base-200 text-base-content flex">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-base-100 border-b border-base-300 shadow-sm">
        <div className="flex items-center justify-between p-4">
          <h1 className="font-bold text-lg">Engine Admin</h1>
          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-lg hover:bg-base-200 transition-colors"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <XMarkIcon className="w-6 h-6" />
            ) : (
              <Bars3Icon className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar Navigation */}
      <aside
        className={`fixed lg:sticky top-0 left-0 h-screen w-56 border-r border-base-300 bg-base-100 shadow-sm flex flex-col z-40 transition-transform duration-300 ${
          mobileMenuOpen
            ? 'translate-x-0'
            : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="sticky top-0 z-20 border-b border-base-300 bg-base-100/90 backdrop-blur p-4 hidden lg:block">
          <h1 className="font-bold text-lg">Engine Admin</h1>
        </div>
        <nav className="flex flex-col p-2 flex-1 mt-16 lg:mt-0 overflow-y-auto">
          {/* Main Navigation */}
          {navItems.map(item => {
            const Icon = item.icon
            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-lg text-sm font-medium transition-colors flex items-center gap-3 ${
                    isActive
                      ? 'bg-primary text-primary-content'
                      : 'text-base-content hover:bg-base-200'
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                <span>{item.label}</span>
              </NavLink>
            )
          })}

          <a
            href="/carriers"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMobileMenuOpen(false)}
            className="px-4 py-3 rounded-lg text-sm font-medium transition-colors flex items-center gap-3 text-base-content hover:bg-base-200"
          >
            <TruckIcon className="w-4 h-4" />
            <span>Carrier Support</span>
            <ArrowTopRightOnSquareIcon className="w-3 h-3 ml-auto opacity-60" />
          </a>

          {/* Reports Section */}
          <div className="mt-6 pt-6 border-t border-base-300">
            <div className="px-4 mb-2">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-base-content/60">
                Reports
              </h2>
            </div>
            {reportItems.map(item => {
              const Icon = item.icon
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-lg text-sm font-medium transition-colors flex items-center gap-3 ${
                      isActive
                        ? 'bg-primary text-primary-content'
                        : 'text-base-content hover:bg-base-200'
                    }`
                  }
                >
                  <Icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </NavLink>
              )
            })}
          </div>
        </nav>
        <div className="border-t border-base-300 p-2 relative z-30">
          <button
            type="button"
            onClick={async () => {
              try {
                await fetch(`${API_BASE}/auth/logout`, {
                  method: 'POST',
                  credentials: 'include',
                })
              } finally {
                window.location.href = '/login'
              }
            }}
            className="w-full px-4 py-3 rounded-lg text-sm font-medium text-base-content hover:bg-base-200 transition-colors text-left flex items-center gap-3"
          >
            <ArrowLeftOnRectangleIcon className="w-4 h-4" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col pt-16 lg:pt-0">
        <div className="flex-1 overflow-auto">
          <div className="mx-auto w-full px-6 py-6">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  )
}
